import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Download,
  Eye,
  EyeOff,
  FileArchive,
  FileText,
  Hand,
  Loader2,
  Mail,
  MoreHorizontal,
  PauseCircle,
  Pencil,
  RefreshCw,
  Tag,
  Upload,
  Wand2,
} from "lucide-react";
import { toast } from "sonner";
import {
  assetUrl,
  downloadAuthed,
  ordersApi,
  pdfUrl,
  printPackageDownloadUrl,
  STATE_LABELS,
  STATE_ORDER,
  type BookSceneRef,
  type GenerationJob,
  type GenerationJobBatch,
  type IndesignJob,
  type OrderDetail as OrderDetailType,
  type OrderItem,
  type OrderState,
  type QualityCheckJob,
  type TimelineRow,
} from "../lib/orders-api";
import AuthAsset from "../components/AuthAsset";
import { EditAddressDialog, EditChildNameDialog } from "../components/EditOrderDialogs";
import { useConfirm, usePrompt } from "@/components/confirm-dialog";
import { DetailLayout, ErrorState, FormSection } from "@/components/layout";
import { PageHeader } from "@/components/page-header";
import {
  MasteringStatusBadge,
  OrderFtpStatusBadge,
  OrderStateBadge,
  StatusBadge,
  type Tone,
} from "@/components/status-badge";
import {
  Alert,
  AlertAction,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";

const FORCE_STATES: OrderState[] = STATE_ORDER;

/** Abstand der automatischen Aktualisierung, solange die Pipeline arbeitet. */
const POLL_MS = 15_000;

export default function OrderDetail() {
  const { id = "" } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const prompt = usePrompt();
  const [data, setData] = useState<OrderDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forceTo, setForceTo] = useState<OrderState | "">("");
  const [forceOpen, setForceOpen] = useState(false);
  const [addressOpen, setAddressOpen] = useState(false);

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    ordersApi
      .get(id)
      .then(setData)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(reload, [reload]);

  // Solange Jobs laufen, still im Hintergrund nachladen (nur lesend). Fehler
  // dabei ignorieren – der nächste Versuch kommt ohnehin.
  const pipelineActive = data ? isPipelineActive(data) : false;
  useEffect(() => {
    if (!pipelineActive) return;
    const timer = window.setInterval(() => {
      if (document.hidden) return;
      ordersApi
        .get(id)
        .then(setData)
        .catch(() => {});
    }, POLL_MS);
    return () => window.clearInterval(timer);
  }, [pipelineActive, id]);

  /**
   * Gemeinsamer Rahmen für Aktionen: sperrt die Buttons, zeigt Erfolg/Fehler
   * als Toast und lädt danach neu. Die API-Aufrufe selbst stehen unverändert
   * in den einzelnen Handlern.
   */
  async function act(
    fn: () => Promise<string | void>,
    opts: { reload?: boolean } = {},
  ) {
    setBusy(true);
    try {
      const message = await fn();
      if (message) toast.success(message);
      if (opts.reload !== false) reload();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function applyEvent(event: string) {
    return act(async () => {
      await ordersApi.transition(id, { event });
      return "Hold aufgehoben";
    });
  }

  function applyForce() {
    if (!forceTo) return;
    return act(async () => {
      await ordersApi.transition(id, {
        event: "manual_override",
        force_to: forceTo as OrderState,
      });
      const label = STATE_LABELS[forceTo as OrderState];
      setForceTo("");
      setForceOpen(false);
      return `Status auf „${label}“ erzwungen`;
    });
  }

  function toggleHidden() {
    if (!data) return;
    const hide = !data.order.hidden;
    return act(async () => {
      await ordersApi.setHidden(id, hide);
      return hide ? "Bestellung versteckt" : "Bestellung wieder eingeblendet";
    });
  }

  function buildPrintPackage() {
    return act(async () => {
      await downloadAuthed(
        printPackageDownloadUrl(id),
        `Druckpaket_${orderNumberSlug()}.zip`,
      );
      return "Druckpaket-ZIP heruntergeladen";
    });
  }

  async function approveReview() {
    const ok = await confirm({
      title: "Druckdaten freigeben?",
      description:
        "• Die Freigabe wird an die aktuellen Cover-/Bookblock-PDFs gebunden.\n" +
        "• Die Mastering-Dateien gehen anschliessend an die Druckerei.\n" +
        "• Wird danach neu gerendert, verfällt die Freigabe automatisch.",
      confirmLabel: "Druckdaten freigeben",
    });
    if (!ok) return;
    await act(async () => {
      await ordersApi.approveReview(id);
      return "Freigegeben – Upload zur Druckerei läuft";
    });
  }

  async function rejectReview() {
    const reason = await prompt({
      title: "Druckdaten ablehnen",
      description:
        "Warum werden die Druckdaten abgelehnt? Es wird nichts zur Druckerei übertragen.",
      placeholder: "Grund (optional)",
      defaultValue: "",
      confirmLabel: "Druckdaten ablehnen",
      destructive: true,
    });
    if (reason === null) return;
    await act(async () => {
      await ordersApi.rejectReview(id, reason);
      return "Druckdaten abgelehnt";
    });
  }

  function resendReview() {
    return act(async () => {
      const res = await ordersApi.resendReview(id);
      if (!res.sent) {
        toast.warning(
          "Mail nicht verschickt (SMTP nicht konfiguriert) – im Log steht der Link.",
        );
        return;
      }
      return "Freigabe-Mail erneut verschickt";
    });
  }

  function sendShippingMail(variant: "shipping" | "return", kind: "test" | "send") {
    return act(async () => {
      if (kind === "send") {
        const ok = await confirm({
          title:
            variant === "return"
              ? "Rücksendungs-Mail an den Kunden senden?"
              : "Versandmail an den Kunden senden?",
          description:
            `Die Mail geht an ${data?.order.customer_email ?? "den Kunden"}. ` +
            "Das ist nur ein einziges Mal möglich – danach ist der Button gesperrt. " +
            "Tipp: vorher die Testmail an shop@star-tales.com prüfen.",
          confirmLabel: "An Kunden senden",
        });
        if (!ok) return;
      }
      const res = await ordersApi.sendShippingMail(id, variant, kind);
      return kind === "test"
        ? `Testmail an ${res.to} gesendet`
        : `Mail an ${res.to} gesendet`;
    });
  }

  async function uploadMastering() {
    const ok = await confirm({
      title: "Mastering-Dateien zur Druckerei hochladen?",
      description:
        "• Das Druckpaket wird zuerst neu gebaut (Cover/Bookblock/Mastering.xml).\n" +
        "• Die Mastering-Dateien werden per SFTP an die Druckerei übertragen.\n" +
        "• Die Bestellung wechselt danach in den Status „Mastering hochgeladen“.",
      confirmLabel: "Mastering hochladen",
    });
    if (!ok) return;
    await act(async () => {
      const res = await ordersApi.uploadMastering(id);
      return `${res.files.length} Mastering-Datei(en) nach ${res.remote_dir} hochgeladen`;
    });
  }

  async function uploadOrder() {
    const ok = await confirm({
      title: "Order-Dateien zur Druckerei hochladen (/order)?",
      description:
        "• Das Druckpaket wird zuerst neu gebaut (Order-XML + Rechnung).\n" +
        "• Die Dateien werden per SFTP an die Druckerei übertragen.\n" +
        "• Die Bestellung wechselt danach in den Status „Order hochgeladen“.\n\n" +
        "Hinweis: Das passiert normalerweise automatisch, sobald die Druckerei " +
        "das Mastering abgenommen hat – das hier ist der manuelle Wiederholversuch.",
      confirmLabel: "Order-Dateien hochladen",
    });
    if (!ok) return;
    await act(async () => {
      const res = await ordersApi.uploadOrder(id);
      return `${res.files.length} Order-Datei(en) nach ${res.remote_dir} hochgeladen`;
    });
  }

  function downloadOrderXml() {
    return act(async () => {
      await downloadAuthed(
        printPackageDownloadUrl(id, true),
        `Bestelldateien_${orderNumberSlug()}.zip`,
      );
      return "XML-Bestelldateien heruntergeladen";
    });
  }

  function orderNumberSlug(): string {
    return data?.order.order_number ?? id.slice(0, 8);
  }

  async function regenerateScenes() {
    const ok = await confirm({
      title: "Alle Szenen dieser Bestellung neu generieren?",
      description:
        "• Die Character-Angles bleiben erhalten.\n" +
        "• Neu aktivierte Szenen des Buches werden zusätzlich erstellt.\n" +
        "• Bestehende Szenen-Bilder, PDFs und das Druckpaket werden ersetzt.",
      confirmLabel: "Alle Szenen neu generieren",
      destructive: true,
    });
    if (!ok) return;
    await act(async () => {
      await ordersApi.regenerateScenes(id);
      return "Szenen werden neu generiert";
    });
  }

  // Manual scene generation for one item. Used both by the per-scene button
  // and the "fehlende Szenen" bulk action; deactivated scenes go through the
  // exact same path (deactivated only stops the automatic run at intake).
  function generateScenes(itemId: string, indices: number[]) {
    return act(async () => {
      const res = await ordersApi.generateScenes(id, itemId, indices);
      if (res.skipped.length > 0) {
        toast.warning(
          `Nicht eingeplant: ${res.skipped.map((s) => `#${s.index} (${s.reason})`).join(", ")}`,
        );
      }
      const planned = indices.length - res.skipped.length;
      if (planned > 0) return `${planned} Szene(n) eingeplant`;
    });
  }

  function retryJob(jobId: string) {
    return act(async () => {
      await ordersApi.retryGenerationJob(id, jobId);
      return "Job wird erneut versucht";
    });
  }

  // Re-run the character-sheet job from the original child photo with the
  // current prompt. Queued, not synchronous: the tile drops back to
  // "wartet"/"läuft" and the page refreshes itself while the job runs.
  async function regenerateCharacterSheet(jobId: string) {
    const ok = await confirm({
      title: "Character-Sheet neu generieren?",
      description:
        "• Basis ist das ursprünglich hochgeladene Kinderfoto.\n" +
        "• Es gilt der aktuelle Prompt aus dem Prompts-Bereich.\n" +
        "• Das bisherige Sheet-Bild wird überschrieben.\n" +
        "• Bereits generierte Szenen bleiben unverändert – dafür danach " +
        "„Alle Szenen neu generieren“ ausführen.",
      confirmLabel: "Sheet neu generieren",
      destructive: true,
    });
    if (!ok) return;
    await act(async () => {
      await ordersApi.regenerateCharacterSheet(id, jobId);
      return "Character-Sheet wird neu generiert";
    });
  }

  // „Outfit vom Foto“: Sheet freigeben (Szenen starten) oder Mail erneut senden.
  function characterSheetAction(itemId: string, action: "approve" | "review-mail") {
    return act(async () => {
      const res = await ordersApi.characterSheetAction(id, itemId, action);
      if (action === "review-mail") {
        return res.sent ? "Freigabe-Mail gesendet" : "Mail nicht gesendet (SMTP nicht konfiguriert?)";
      }
      return res.resumed
        ? "Sheet freigegeben – Szenen werden generiert"
        : `Sheet freigegeben – ${res.waitingFor} weitere(s) Sheet(s) warten noch`;
    });
  }

  // Parked order („Outfit vom Foto“): generate only the character sheet to
  // check it before lifting the hold. The order stays paused.
  function generateCharacterSheet() {
    return act(async () => {
      await ordersApi.generateCharacterSheet(id);
      return "Character-Sheet wird generiert – Szenen folgen erst nach „Hold aufheben“";
    });
  }

  // Edit a character-angle image in place via OpenAI. Throws on failure so the
  // inline form can surface the message; no full reload needed — the tile busts
  // its own image cache on success.
  async function editAngleImage(jobId: string, prompt: string) {
    setBusy(true);
    try {
      await ordersApi.editJobImage(id, jobId, prompt);
      toast.success("Bild bearbeitet");
    } finally {
      setBusy(false);
    }
  }

  function retryIndesign(jobId: string) {
    return act(async () => {
      await ordersApi.retryIndesignJob(id, jobId);
      return "InDesign-Job neu eingeplant";
    });
  }

  function startIndesign() {
    return act(async () => {
      const res = await ordersApi.startIndesign(id);
      if (res.inserted === 0) {
        toast.info(
          "Keine neuen InDesign-Jobs angelegt – Jobs existieren bereits oder kein Item hat eine fields.csv.",
        );
        return;
      }
      return `${res.inserted} InDesign-Job(s) angelegt`;
    });
  }

  // InDesign vor den Szenen: fehlende Bilder werden zu Vorlagen-Platzhaltern.
  // Die PDFs sind nur zum Anschauen — Freigabe/Druckerei sind gesperrt, bis
  // mit allen Szenen neu gerendert wurde (passiert nach den Szenen automatisch).
  async function startIndesignPlaceholder() {
    const ok = await confirm({
      title: "InDesign mit Platzhaltern starten?",
      description:
        "• Fehlende Szenen werden mit den Platzhaltern der Vorlage gerendert.\n" +
        "• Die PDFs sind nur zur Ansicht – Freigabe und Drucker-Upload sind gesperrt.\n" +
        "• Sobald alle Szenen fertig sind, wird automatisch neu gerendert.\n" +
        "• Vorhandene PDFs dieser Bestellung werden ersetzt.",
      confirmLabel: "Mit Platzhaltern rendern",
    });
    if (!ok) return;
    await act(async () => {
      const r = await ordersApi.startIndesignPlaceholder(id);
      return r.missing_scenes > 0
        ? `InDesign eingeplant – ${r.missing_scenes} Szene(n) als Platzhalter`
        : "InDesign eingeplant – alle Szenen vorhanden, regulärer Render";
    });
  }

  function qcRecheck(qcJobId: string) {
    return act(async () => {
      await ordersApi.recheckQuality(id, qcJobId);
      return "Qualitätscheck neu gestartet";
    });
  }

  function qcApprove(qcJobId: string) {
    return act(async () => {
      await ordersApi.approveQuality(id, qcJobId);
      return "Szene manuell freigegeben";
    });
  }

  function qcRegenerate(qcJobId: string) {
    return act(async () => {
      await ordersApi.regenerateSceneForQuality(id, qcJobId);
      return "Szene wird neu generiert";
    });
  }

  if (loading && !data) return <OrderDetailSkeleton />;
  if (error && !data)
    return (
      <div>
        <PageHeader
          backTo="/orders"
          backLabel="Zurück zu Bestellungen"
          title="Bestellung"
        />
        <ErrorState message={error} onRetry={reload} />
      </div>
    );
  if (!data) return null;

  const {
    order,
    items,
    generation_jobs,
    indesign_jobs,
    quality_check_jobs,
    book_scenes,
    timeline,
  } = data;

  const printReady = canBuildPrintPackage(items, indesign_jobs);
  const indesignReady = canStartIndesign(items);
  const notReadyHint = "erst wenn alle InDesign-PDFs fertig sind";

  return (
    <div>
      <PageHeader
        backTo="/orders"
        backLabel="Zurück zu Bestellungen"
        title={order.order_number}
        badges={
          <>
            <OrderStateBadge state={order.state} />
            <MasteringStatusBadge
              status={order.mastering_status}
              title="Mastering-Upload-Status"
            />
            <OrderFtpStatusBadge
              status={order.order_status}
              title="Order-Upload-Status (/order)"
            />
            {order.hidden && (
              <StatusBadge tone="neutral">Versteckt</StatusBadge>
            )}
          </>
        }
        meta={
          <span className="flex flex-wrap items-center gap-x-2">
            Eingegangen {formatDateTime(order.created_at)}
            {pipelineActive && (
              <span className="flex items-center gap-1 text-ring">
                <RefreshCw
                  className="size-3 animate-spin [animation-duration:3s]"
                  aria-hidden
                />
                aktualisiert automatisch
              </span>
            )}
          </span>
        }
        actions={
          <>
            <Button
              variant="secondary"
              size="icon"
              onClick={reload}
              disabled={loading}
              aria-label="Aktualisieren"
              title="Aktualisieren"
            >
              <RefreshCw className={loading ? "animate-spin" : undefined} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="secondary" disabled={busy}>
                  {busy ? (
                    <Loader2 className="animate-spin" />
                  ) : (
                    <MoreHorizontal />
                  )}
                  Weitere Aktionen
                  <ChevronDown />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-72">
                <DropdownMenuLabel>Druckerei</DropdownMenuLabel>
                <MenuAction
                  icon={FileArchive}
                  label="Druckpaket herunterladen"
                  hint={printReady ? "PDFs + XMLs als ZIP" : notReadyHint}
                  disabled={!printReady}
                  onSelect={buildPrintPackage}
                />
                <MenuAction
                  icon={Download}
                  label="XML-Bestelldateien herunterladen"
                  hint={printReady ? "nur Order-/Mastering-XML" : notReadyHint}
                  disabled={!printReady}
                  onSelect={downloadOrderXml}
                />
                <MenuAction
                  icon={Upload}
                  label="Mastering hochladen"
                  hint={printReady ? "per SFTP zur Druckerei" : notReadyHint}
                  disabled={!printReady}
                  onSelect={uploadMastering}
                />
                <MenuAction
                  icon={Upload}
                  label="Order-Dateien hochladen"
                  hint="manueller Wiederholversuch für /order"
                  onSelect={uploadOrder}
                />
                <DropdownMenuSeparator />
                <DropdownMenuLabel>Produktion</DropdownMenuLabel>
                <MenuAction
                  icon={FileText}
                  label="InDesign starten"
                  hint={
                    indesignReady
                      ? "legt fehlende Jobs an, bestehende bleiben"
                      : "erst wenn die fields.csv generiert wurde"
                  }
                  disabled={!indesignReady}
                  onSelect={startIndesign}
                />
                <MenuAction
                  icon={FileText}
                  label="InDesign mit Platzhaltern"
                  hint="fehlende Szenen als Vorlagen-Platzhalter, nur zur Ansicht"
                  onSelect={startIndesignPlaceholder}
                />
                <MenuAction
                  icon={Wand2}
                  label="Alle Szenen neu generieren"
                  hint="Angles bleiben, PDFs + Druckpaket werden ersetzt"
                  onSelect={regenerateScenes}
                />
                <DropdownMenuSeparator />
                <MenuAction
                  icon={Tag}
                  label="Status erzwingen…"
                  hint="setzt den Status ohne Folgeschritte"
                  onSelect={() => setForceOpen(true)}
                />
                <MenuAction
                  icon={order.hidden ? Eye : EyeOff}
                  label={order.hidden ? "Einblenden" : "Verstecken"}
                  hint={
                    order.hidden
                      ? "wieder in der Liste zeigen"
                      : "aus der Liste ausblenden (nicht löschen)"
                  }
                  onSelect={toggleHidden}
                />
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      {order.state === "on_hold" && (
        <Alert variant="warning" className="mb-4">
          <PauseCircle />
          <AlertTitle>Bestellung ist pausiert</AlertTitle>
          <AlertDescription>
            {items.some(
              (i) => i.outfit_choice === "photo" && !i.character_sheet_approved_at,
            )
              ? "„Outfit vom Foto“ – das Character-Sheet wartet auf die Freigabe (Mail oder „Sheet freigeben“ bei der Position). "
              : ""}
            Die Pipeline läuft erst weiter, wenn der Hold aufgehoben wird. Lief
            noch keine Generierung, startet sie mit dem Aufheben. Das
            Character-Sheet lässt sich vorab einzeln generieren und prüfen.
          </AlertDescription>
          <AlertAction className="flex flex-wrap gap-2">
            {!generation_jobs.some((j) => j.kind === "character_angle") && (
              <Button
                size="sm"
                variant="outline"
                onClick={generateCharacterSheet}
                disabled={busy}
              >
                Character-Sheet generieren
              </Button>
            )}
            <Button
              size="sm"
              onClick={() => applyEvent("resume_from_hold")}
              disabled={busy}
            >
              Hold aufheben
            </Button>
          </AlertAction>
        </Alert>
      )}

      <DetailLayout
        main={
          <>
            {/* Freigabe. Der Uploader verweigert jede Übertragung ohne gültige
                Freigabe – deshalb steht sie ganz oben. */}
            <FormSection title="Freigabe der Druckdaten">
              <ReviewState order={order} />
              <div className="flex flex-wrap gap-2">
                <HintButton
                  variant={order.review_approved_at ? "secondary" : "default"}
                  hint={
                    printReady
                      ? "Gibt die aktuellen Druckdaten frei und stösst den Mastering-Upload an."
                      : `Freigabe ${notReadyHint}.`
                  }
                  onClick={approveReview}
                  disabled={busy || !printReady}
                >
                  <CheckCircle2 />
                  Druckdaten freigeben
                </HintButton>
                <HintButton
                  variant="secondary"
                  hint="Lehnt die Druckdaten ab. Es wird nichts übertragen."
                  onClick={rejectReview}
                  disabled={busy}
                >
                  Ablehnen
                </HintButton>
                <HintButton
                  variant="secondary"
                  hint={
                    printReady
                      ? "Verschickt die Freigabe-Mail mit dem Handy-Link erneut."
                      : `Mail ${notReadyHint}.`
                  }
                  onClick={resendReview}
                  disabled={busy || !printReady}
                >
                  Freigabe-Mail senden
                </HintButton>
              </div>
              {!printReady && (
                <p className="text-xs text-muted-foreground">
                  Freigabe und Druckpaket sind möglich, sobald alle
                  InDesign-PDFs fertig sind.
                </p>
              )}
            </FormSection>

            <FormSection title="Fortschritt">
              <ProgressOverview
                items={items}
                generationJobs={generation_jobs}
                indesignJobs={indesign_jobs}
                qualityChecks={quality_check_jobs}
                bookScenes={book_scenes ?? []}
              />
            </FormSection>
          </>
        }
        aside={
          <>
            <FormSection title="Bestellung" size="sm">
              <MetaList>
                <MetaRow label="Shopify">
                  {order.shopify_order_number ?? "—"}{" "}
                  <span className="text-muted-foreground">
                    ({order.shopify_order_id ?? "—"})
                  </span>
                  {/* Bestellnummer als Tag + Notiz an der Shopify-Order? */}
                  {order.shopify_writeback_at ? (
                    <InlineStatus
                      tone="success"
                      title={`Bestellnummer in Shopify hinterlegt am ${formatDateTime(order.shopify_writeback_at)}`}
                    >
                      Nr. hinterlegt
                    </InlineStatus>
                  ) : order.shopify_writeback_error ? (
                    <InlineStatus
                      tone="critical"
                      title={order.shopify_writeback_error}
                    >
                      Nr. nicht hinterlegt
                    </InlineStatus>
                  ) : null}
                </MetaRow>
                <MetaRow label="Shop">{order.shop}</MetaRow>
                <MetaRow label="Kunde">
                  <span className="break-all">
                    {order.customer_email ?? "—"}
                  </span>
                </MetaRow>
                <MetaRow label="Eingegangen">
                  {formatDateTime(order.created_at)}
                </MetaRow>
                <MetaRow label="Status seit">
                  {formatDateTime(order.state_changed_at)}
                </MetaRow>
              </MetaList>
            </FormSection>

            {(order.shipped_at || order.tracking_number) && (
              <FormSection
                title="Versand"
                size="sm"
              >
                <MetaList>
                  {order.shipped_at && (
                    <MetaRow label="Versandt">
                      {formatDateTime(order.shipped_at)}
                      {order.carrier ? ` · ${order.carrier}` : ""}
                      {/* In Shopify als versendet markiert? Das ist der Schritt, der
                          die Versandbestätigung an den Kunden auslöst. */}
                      {order.shopify_fulfilled_at ? (
                        <InlineStatus
                          tone="success"
                          title={`In Shopify als versendet markiert am ${formatDateTime(order.shopify_fulfilled_at)}`}
                        >
                          in Shopify versendet
                        </InlineStatus>
                      ) : order.shopify_fulfillment_error ? (
                        <InlineStatus
                          tone="critical"
                          title={order.shopify_fulfillment_error}
                        >
                          Shopify nicht aktualisiert
                        </InlineStatus>
                      ) : (
                        <InlineStatus tone="neutral">
                          Shopify offen
                        </InlineStatus>
                      )}
                    </MetaRow>
                  )}
                  {order.tracking_number && (
                    <MetaRow label="Sendungsnr.">
                      {trackingUrl(order.carrier, order.tracking_number) ? (
                        <a
                          href={trackingUrl(
                            order.carrier,
                            order.tracking_number,
                          )!}
                          target="_blank"
                          rel="noreferrer"
                          className="font-mono break-all text-ring hover:underline"
                        >
                          {order.tracking_number}
                        </a>
                      ) : (
                        <code className="font-mono break-all">
                          {order.tracking_number}
                        </code>
                      )}
                    </MetaRow>
                  )}
                </MetaList>
                <div className="mt-4 space-y-3 border-t pt-4">
                  {(
                    [
                      {
                        variant: "shipping",
                        label: "Versandbenachrichtigung",
                        sentAt: order.customer_shipping_mail_sent_at,
                      },
                      {
                        variant: "return",
                        label: "Rücksendung (Paket kam zurück)",
                        sentAt: order.customer_return_mail_sent_at,
                      },
                    ] as const
                  ).map((m) => (
                    <div key={m.variant} className="space-y-1.5">
                      <div className="text-muted-foreground">{m.label}</div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => sendShippingMail(m.variant, "test")}
                          title="Testmail an shop@star-tales.com – beliebig oft möglich"
                        >
                          <Mail />
                          Test an mich
                        </Button>
                        <Button
                          size="sm"
                          disabled={
                            busy ||
                            !!m.sentAt ||
                            !order.customer_email ||
                            order.state !== "shipped" ||
                            !order.tracking_number
                          }
                          onClick={() => sendShippingMail(m.variant, "send")}
                          title={
                            m.sentAt
                              ? `Bereits gesendet am ${formatDateTime(m.sentAt)}`
                              : order.state !== "shipped"
                                ? "Die Druckerei hat den Versand noch nicht bestätigt"
                                : !order.tracking_number
                                ? "Noch keine Sendungsnummer"
                                : !order.customer_email
                                  ? "Keine Kunden-E-Mail vorhanden"
                                  : "Mail an den Kunden senden (einmalig)"
                          }
                        >
                          <Mail />
                          {m.sentAt ? "Mail gesendet" : "An Kunden senden"}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </FormSection>
            )}

            <FormSection
              title="Lieferadresse"
              size="sm"
              actions={
                <Button size="sm" variant="secondary" onClick={() => setAddressOpen(true)}>
                  <Pencil />
                  Bearbeiten
                </Button>
              }
            >
              {order.shipping_address ? (
                <ShippingAddress address={order.shipping_address} />
              ) : (
                <p className="text-muted-foreground">Keine Adresse hinterlegt.</p>
              )}
            </FormSection>
            <EditAddressDialog
              open={addressOpen}
              onOpenChange={setAddressOpen}
              orderId={order.id}
              address={order.shipping_address}
              printerUploaded={order.order_status !== "not_uploaded"}
              onSaved={reload}
            />

            {order.notes && (
              <FormSection title="Notizen" size="sm">
                <p className="whitespace-pre-wrap">{order.notes}</p>
              </FormSection>
            )}

            <FormSection title="Verlauf" size="sm">
              <Timeline rows={timeline ?? []} />
            </FormSection>
          </>
        }
      />

      <div className="mt-6 space-y-4">
        <h2 className="text-base font-semibold">Positionen ({items.length})</h2>
        {items.length === 0 ? (
          <Card>
            <CardContent className="text-muted-foreground">
              Keine Positionen.
            </CardContent>
          </Card>
        ) : (
          items.map((it) => (
            <ItemBlock
              key={it.id}
              orderId={order.id}
              item={it}
              jobs={generation_jobs.filter((j) => j.order_item_id === it.id)}
              bookScenes={(book_scenes ?? []).filter(
                (s) => s.book_id === it.book_id,
              )}
              qualityChecks={quality_check_jobs.filter(
                (q) => q.order_item_id === it.id,
              )}
              indesignJob={
                indesign_jobs.find((j) => j.order_item_id === it.id) ?? null
              }
              onRetry={retryJob}
              onEditImage={editAngleImage}
              onRegenerateSheet={regenerateCharacterSheet}
              onGenerateScenes={generateScenes}
              onCharacterSheetAction={characterSheetAction}
              onRetryIndesign={retryIndesign}
              onQcRecheck={qcRecheck}
              onQcApprove={qcApprove}
              onQcRegenerate={qcRegenerate}
              busy={busy}
              onReload={reload}
            />
          ))
        )}
      </div>

      <Dialog open={forceOpen} onOpenChange={setForceOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Status erzwingen</DialogTitle>
            <DialogDescription>
              Setzt den Status der Bestellung direkt („manual_override“). Es
              werden keine weiteren Schritte ausgelöst.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label htmlFor="force-state">Neuer Status</Label>
            <Select
              value={forceTo}
              onValueChange={(v) => setForceTo(v as OrderState | "")}
            >
              <SelectTrigger id="force-state" className="w-full bg-card">
                <SelectValue placeholder="Status wählen…" />
              </SelectTrigger>
              <SelectContent>
                {FORCE_STATES.map((s) => (
                  <SelectItem value={s} key={s} disabled={s === order.state}>
                    {STATE_LABELS[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="secondary" onClick={() => setForceOpen(false)}>
              Abbrechen
            </Button>
            <Button onClick={applyForce} disabled={!forceTo || busy}>
              Status erzwingen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Läuft gerade irgendetwas, auf das man warten würde? Dann pollt die Seite. */
function isPipelineActive(d: OrderDetailType): boolean {
  return (
    d.generation_jobs.some(
      (j) => j.state === "pending" || j.state === "running",
    ) ||
    d.indesign_jobs.some(
      (j) =>
        j.state === "pending" ||
        j.state === "claimed" ||
        j.state === "rendering",
    ) ||
    d.quality_check_jobs.some(
      (q) => q.state === "pending" || q.state === "checking",
    )
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString("de-DE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

function MenuAction({
  icon: Icon,
  label,
  hint,
  disabled,
  onSelect,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  hint?: string;
  disabled?: boolean;
  onSelect: () => void;
}) {
  return (
    <DropdownMenuItem
      disabled={disabled}
      onSelect={onSelect}
      className="items-start"
    >
      <Icon className="mt-0.5" />
      <span className="flex flex-col">
        <span>{label}</span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      </span>
    </DropdownMenuItem>
  );
}

/** Kompakter Stand je Position – Details stehen in den Positionen weiter unten. */
function ProgressOverview({
  items,
  generationJobs,
  indesignJobs,
  qualityChecks,
  bookScenes,
}: {
  items: OrderItem[];
  generationJobs: GenerationJob[];
  indesignJobs: IndesignJob[];
  qualityChecks: QualityCheckJob[];
  bookScenes: BookSceneRef[];
}) {
  if (items.length === 0)
    return <p className="text-muted-foreground">Keine Positionen.</p>;
  return (
    <div className="space-y-4">
      {items.map((it) => {
        const jobs = generationJobs.filter((j) => j.order_item_id === it.id);
        const angles = summarize(
          jobs.filter((j) => j.kind === "character_angle"),
        );
        const scenes = summarizeScenes(
          buildSceneEntries(
            bookScenes.filter((s) => s.book_id === it.book_id),
            jobs.filter((j) => j.kind === "scene"),
          ),
        );
        const indesign =
          indesignJobs.find((j) => j.order_item_id === it.id) ?? null;
        const qcIssues = qualityChecks.filter(
          (q) =>
            q.order_item_id === it.id &&
            (q.state === "needs_rework" || q.state === "failed"),
        ).length;
        return (
          <div key={it.id} className="space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-medium">{it.child_name}</span>
              <span className="text-xs text-muted-foreground">
                Titel {it.title_number}
              </span>
              {it.outfit_choice === "photo" && <OutfitBadge choice="photo" />}
              <span className="ml-auto flex flex-wrap gap-1">
                {qcIssues > 0 && (
                  <StatusBadge tone="critical">
                    {qcIssues} QC-Problem(e)
                  </StatusBadge>
                )}
                <StatusBadge
                  tone={
                    indesign ? INDESIGN_STATE_TONE[indesign.state] : "neutral"
                  }
                >
                  InDesign:{" "}
                  {indesign
                    ? INDESIGN_STATE_LABELS[indesign.state]
                    : "noch kein Job"}
                </StatusBadge>
              </span>
            </div>
            <ProgressRow label="Character-Angles" summary={angles} />
            <ProgressRow label="Szenen" summary={scenes} />
          </div>
        );
      })}
    </div>
  );
}

/** Lieferadresse wie auf einem Etikett; die Rohdaten bleiben aufklappbar. */
function ShippingAddress({ address }: { address: Record<string, unknown> }) {
  const str = (k: string) => {
    const v = address[k];
    return typeof v === "string" && v.trim() ? v.trim() : null;
  };
  const name =
    str("name") ??
    ([str("first_name"), str("last_name")].filter(Boolean).join(" ") || null);
  const cityLine = [str("zip"), str("city")].filter(Boolean).join(" ");
  const lines = [
    name,
    str("company"),
    str("address1"),
    str("address2"),
    cityLine || null,
    str("province"),
    str("country"),
  ].filter(Boolean) as string[];
  const phone = str("phone");

  return (
    <div className="space-y-2">
      {lines.length > 0 ? (
        <address className="not-italic leading-relaxed">
          {lines.map((l, i) => (
            <div key={i}>{l}</div>
          ))}
          {phone && <div className="text-muted-foreground">Tel. {phone}</div>}
        </address>
      ) : (
        <p className="text-muted-foreground">Keine lesbare Adresse.</p>
      )}
      <Collapsible>
        <CollapsibleTrigger className="group flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
          <ChevronRight className="size-3.5 transition-transform group-data-[state=open]:rotate-90" />
          Rohdaten
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-2">
          <CodeBlock>{JSON.stringify(address, null, 2)}</CodeBlock>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}

const ACTOR_LABELS: Record<TimelineRow["actor"], string> = {
  system: "System",
  founder: "Dashboard",
  printer: "Druckerei",
  shopify: "Shopify",
};

/** Ereignisse der Bestellung, neueste zuerst. */
function Timeline({ rows }: { rows: TimelineRow[] }) {
  const [showAll, setShowAll] = useState(false);
  const sorted = [...rows].sort((a, b) => b.ts.localeCompare(a.ts));
  const visible = showAll ? sorted : sorted.slice(0, 5);
  if (rows.length === 0)
    return <p className="text-muted-foreground">Noch keine Ereignisse.</p>;
  return (
    <div className="space-y-2">
      <ol className="space-y-2.5 border-l pl-3">
        {visible.map((r) => (
          <li key={r.id} className="relative">
            <span className="absolute top-1.5 -left-[16.5px] size-2 rounded-full bg-border ring-2 ring-card" />
            <div className="font-mono text-xs font-medium break-words">
              {r.event}
            </div>
            <div className="text-xs text-muted-foreground">
              {formatDateTime(r.ts)} · {ACTOR_LABELS[r.actor] ?? r.actor}
            </div>
          </li>
        ))}
      </ol>
      {sorted.length > 5 && (
        <Button variant="link" size="sm" onClick={() => setShowAll((s) => !s)}>
          {showAll ? "Weniger anzeigen" : `Alle ${sorted.length} anzeigen`}
        </Button>
      )}
    </div>
  );
}

// ── Layout-Helfer ────────────────────────────────────────────────────────────

function OrderDetailSkeleton() {
  return (
    <div aria-busy="true" aria-label="Lade Order">
      <div className="mb-4 flex items-center gap-3">
        <Skeleton className="size-7" />
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-5 w-24" />
      </div>
      <div className="grid gap-4 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          <Skeleton className="h-36 rounded-xl" />
          <Skeleton className="h-52 rounded-xl" />
        </div>
        <div className="space-y-4">
          <Skeleton className="h-48 rounded-xl" />
          <Skeleton className="h-32 rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/**
 * Button mit Erklärung. Deaktivierte Buttons bekommen keine Maus-Events,
 * deshalb hängt der Hinweis (title) am umgebenden Element.
 */
function HintButton({
  hint,
  ...props
}: React.ComponentProps<typeof Button> & { hint: string }) {
  return (
    <span title={hint} className="inline-flex">
      <Button {...props} />
    </span>
  );
}

function MetaList({ children }: { children: React.ReactNode }) {
  return (
    <dl className="grid grid-cols-[96px_1fr] gap-x-3 gap-y-2">{children}</dl>
  );
}

function MetaRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="m-0 min-w-0">{children}</dd>
    </>
  );
}

function InlineStatus({
  tone,
  title,
  children,
}: {
  tone: "success" | "critical" | "neutral";
  title?: string;
  children: React.ReactNode;
}) {
  const Icon =
    tone === "success"
      ? CheckCircle2
      : tone === "critical"
        ? AlertTriangle
        : Clock;
  return (
    <span
      title={title}
      className={cn(
        "mt-0.5 flex items-center gap-1 text-xs",
        tone === "success" && "text-success-foreground",
        tone === "critical" && "text-destructive",
        tone === "neutral" && "text-muted-foreground",
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {children}
    </span>
  );
}

function CodeBlock({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <pre
      className={cn(
        "m-0 max-h-80 overflow-auto rounded-lg bg-muted p-3 font-mono text-xs leading-relaxed whitespace-pre-wrap break-words",
        className,
      )}
    >
      {children}
    </pre>
  );
}

function SectionTitle({
  children,
  hint,
  actions,
}: {
  children: React.ReactNode;
  hint?: React.ReactNode;
  actions?: React.ReactNode;
}) {
  return (
    <div className="mb-2 flex flex-wrap items-center gap-x-2 gap-y-1">
      <h3 className="text-sm font-semibold">{children}</h3>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      {actions && (
        <div className="ml-auto flex flex-wrap gap-1.5">{actions}</div>
      )}
    </div>
  );
}

function ReviewState({ order }: { order: OrderDetailType["order"] }) {
  if (order.review_approved_at) {
    return (
      <Alert variant="success">
        <CheckCircle2 />
        <AlertDescription>
          Freigegeben{" "}
          {order.review_approved_by === "mobile" ? "(Handy)" : "(Dashboard)"} ·{" "}
          {new Date(order.review_approved_at).toLocaleString("de-AT")}
        </AlertDescription>
      </Alert>
    );
  }
  if (order.review_rejected_at) {
    return (
      <Alert variant="critical">
        <Hand />
        <AlertDescription>
          Abgelehnt ·{" "}
          {new Date(order.review_rejected_at).toLocaleString("de-AT")}
          {order.review_reject_reason
            ? ` — „${order.review_reject_reason}“`
            : ""}
        </AlertDescription>
      </Alert>
    );
  }
  if (order.review_requested_at) {
    return (
      <Alert variant="warning">
        <Clock />
        <AlertDescription>
          Wartet auf Freigabe (Mail verschickt{" "}
          {new Date(order.review_requested_at).toLocaleString("de-AT")}) — ohne
          Freigabe geht nichts zur Druckerei.
        </AlertDescription>
      </Alert>
    );
  }
  return (
    <p className="text-muted-foreground">Noch keine Freigabe angefordert.</p>
  );
}

// ── Items ───────────────────────────────────────────────────────────────────

function ItemBlock({
  orderId,
  item,
  jobs,
  bookScenes,
  qualityChecks,
  indesignJob,
  onRetry,
  onEditImage,
  onRegenerateSheet,
  onGenerateScenes,
  onCharacterSheetAction,
  onRetryIndesign,
  onQcRecheck,
  onQcApprove,
  onQcRegenerate,
  busy,
  onReload,
}: {
  orderId: string;
  item: OrderItem;
  jobs: GenerationJob[];
  bookScenes: BookSceneRef[];
  qualityChecks: QualityCheckJob[];
  indesignJob: IndesignJob | null;
  onRetry: (jobId: string) => void;
  onEditImage: (jobId: string, prompt: string) => Promise<void>;
  onRegenerateSheet: (jobId: string) => void;
  onGenerateScenes: (itemId: string, indices: number[]) => void;
  onCharacterSheetAction: (itemId: string, action: "approve" | "review-mail") => void;
  onRetryIndesign: (jobId: string) => void;
  onQcRecheck: (qcJobId: string) => void;
  onQcApprove: (qcJobId: string) => void;
  onQcRegenerate: (qcJobId: string) => void;
  busy: boolean;
  onReload: () => void;
}) {
  const angles = jobs
    .filter((j) => j.kind === "character_angle")
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
  const scenes = jobs
    .filter((j) => j.kind === "scene")
    .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));

  const [nameOpen, setNameOpen] = useState(false);
  // Exklusive Outfit-Szenen gehören nur zu Positionen, die das Foto-Outfit
  // wirklich tragen — sonst sind sie nicht Teil des Buchs.
  const wearsPhotoOutfit =
    item.outfit_choice === "photo" && item.photo_outfit_fallback !== true;
  const sceneEntries = buildSceneEntries(
    bookScenes.filter((s) => wearsPhotoOutfit || !s.photo_outfit_only),
    scenes,
  );
  const angleSummary = summarize(angles);
  // Counts the whole scene catalog, not just the jobs that happen to exist —
  // otherwise an order whose scenes were never queued reads as "0/0 = done".
  const sceneSummary = summarizeScenes(sceneEntries);

  return (
    <Card>
      <CardHeader className="border-b">
        <CardTitle className="flex items-center gap-1 text-base">
          {item.child_name}
          <Button
            size="icon-sm"
            variant="ghost"
            aria-label="Name bearbeiten"
            onClick={() => setNameOpen(true)}
          >
            <Pencil />
          </Button>
          <OutfitBadge choice={item.outfit_choice} className="ml-1" />
        </CardTitle>
        <CardDescription>
          {item.gender} · Titel {item.title_number} · {item.hair_color ?? "—"}{" "}
          Haar · {item.eye_color ?? "—"} Augen · {item.copies}×
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-3">
        {item.outfit_choice === "photo" && (
          <PhotoOutfitStatus
            item={item}
            angles={angles}
            scenes={scenes}
            bookScenes={bookScenes}
            busy={busy}
            onAction={(action) => onCharacterSheetAction(item.id, action)}
            onGenerateScenes={(indices) => onGenerateScenes(item.id, indices)}
          />
        )}
        <EditChildNameDialog
          open={nameOpen}
          onOpenChange={setNameOpen}
          orderId={orderId}
          itemId={item.id}
          childName={item.child_name}
          onSaved={onReload}
        />
        <ItemSection
          title="Bilder"
          summary={
            <StatusBadge
              tone={
                sceneSummary.failed > 0 || angleSummary.failed > 0
                  ? "critical"
                  : sceneSummary.total > 0 &&
                      sceneSummary.succeeded === sceneSummary.total
                    ? "success"
                    : "info"
              }
            >
              {sceneSummary.succeeded}/{sceneSummary.total} Szenen
            </StatusBadge>
          }
          defaultOpen
        >
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="w-full shrink-0 sm:w-40">
              <SectionTitle>Quellbild</SectionTitle>
              <figure className="m-0 overflow-hidden rounded-lg border-2 border-ring/40 bg-card">
                {item.preview_character_url ? (
                  <a
                    href={item.preview_character_url}
                    target="_blank"
                    rel="noreferrer"
                    title="Original öffnen"
                    className="block"
                  >
                    <img
                      className="aspect-square w-full object-cover"
                      src={item.preview_character_url}
                      alt="Kunden-Preview des Characters"
                    />
                  </a>
                ) : (
                  <div className={TILE_PLACEHOLDER}>kein Preview</div>
                )}
                <figcaption className="px-2 py-1.5 text-xs text-muted-foreground">
                  Kunden-Preview
                </figcaption>
              </figure>
            </div>

            {angles.length > 0 && (
              <div className="min-w-0 flex-1">
                <AssetRow
                  title="Character-Angles"
                  jobs={angles}
                  orderId={orderId}
                  itemId={item.id}
                  kind="angles"
                  onRetry={onRetry}
                  onEditImage={onEditImage}
                  onRegenerateSheet={onRegenerateSheet}
                  busy={busy}
                />
              </div>
            )}
          </div>

          <SceneRow
            entries={sceneEntries}
            orderId={orderId}
            itemId={item.id}
            onGenerate={(indices) => onGenerateScenes(item.id, indices)}
            busy={busy}
            qualityChecks={qualityChecks}
            onQcRecheck={onQcRecheck}
            onQcApprove={onQcApprove}
            onQcRegenerate={onQcRegenerate}
          />
        </ItemSection>

        <ItemSection
          title="Texte"
          summary={
            <span className="text-xs text-muted-foreground">
              Widmung {item.dedication ? "vorhanden" : "keine"} ·{" "}
              {item.outfit_choice === "photo" &&
                `Foto-Outfit ${item.photo_outfit_description ? "✓" : "—"} · `}
              Outfit{" "}
              {item.outfit_description ? "✓" : "—"} · Identity{" "}
              {item.char_identity ? "✓" : "—"}
            </span>
          }
        >
          <div className="grid gap-5 lg:grid-cols-2">
            <DedicationEditor
              orderId={orderId}
              item={item}
              busy={busy}
              onReload={onReload}
            />
            {item.outfit_choice === "photo" && (
              <PhotoOutfitDescriptionEditor
                orderId={orderId}
                item={item}
                busy={busy}
                onReload={onReload}
              />
            )}
            <OutfitDescriptionEditor
              orderId={orderId}
              item={item}
              busy={busy}
              onReload={onReload}
            />
            <CharIdentityEditor
              orderId={orderId}
              item={item}
              busy={busy}
              onReload={onReload}
            />
          </div>
        </ItemSection>

        <ItemSection
          title="InDesign"
          summary={
            indesignJob ? (
              <StatusBadge tone={INDESIGN_STATE_TONE[indesignJob.state]}>
                {INDESIGN_STATE_LABELS[indesignJob.state]}
              </StatusBadge>
            ) : (
              <span className="text-xs text-muted-foreground">
                noch kein Job
              </span>
            )
          }
          defaultOpen={indesignJob?.state === "failed"}
        >
          <IndesignBlock
            orderId={orderId}
            itemId={item.id}
            job={indesignJob}
            onRetry={onRetryIndesign}
            busy={busy}
          />
        </ItemSection>
      </CardContent>
    </Card>
  );
}

/** Aufklappbarer Abschnitt einer Position; die Zusammenfassung bleibt sichtbar. */
function ItemSection({
  title,
  summary,
  defaultOpen = false,
  children,
}: {
  title: string;
  summary?: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="border-t pt-3 first:border-t-0 first:pt-0"
    >
      <CollapsibleTrigger className="group flex w-full items-center gap-2 rounded-md text-left outline-none focus-visible:ring-3 focus-visible:ring-ring/50">
        <ChevronRight
          className="size-4 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
          aria-hidden
        />
        <span className="font-semibold">{title}</span>
        {summary && (
          <span className="ml-auto flex items-center">{summary}</span>
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-5 pt-4">
        {children}
      </CollapsibleContent>
    </Collapsible>
  );
}

/** Gemeinsames Gerüst der drei Text-Editoren (Widmung, Outfit, Identity). */
function TextFieldEditor({
  title,
  hint,
  value,
  emptyText,
  addLabel,
  rows,
  mono,
  placeholder,
  editHint,
  busy,
  editing,
  setEditing,
  draft,
  setDraft,
  saving,
  error,
  onSave,
  onCancel,
  footer,
}: {
  title: string;
  hint: React.ReactNode;
  value: string | null;
  emptyText: string;
  /** Eigener Button bei leerem Wert (nur Widmung). */
  addLabel?: string;
  rows: number;
  mono?: boolean;
  placeholder?: string;
  editHint: string;
  busy: boolean;
  editing: boolean;
  setEditing: (v: boolean) => void;
  draft: string;
  setDraft: (v: string) => void;
  saving: boolean;
  error: string | null;
  onSave: () => void;
  onCancel: () => void;
  footer?: React.ReactNode;
}) {
  useUnsavedChanges(editing && draft !== (value ?? ""));
  const empty = !value;
  const fieldId = `field-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`;
  return (
    <section>
      <SectionTitle hint={hint}>
        <label htmlFor={editing ? fieldId : undefined}>{title}</label>
      </SectionTitle>
      {empty && !editing ? (
        <p className="text-muted-foreground">{emptyText}</p>
      ) : editing ? (
        <div className="space-y-2">
          <Textarea
            id={fieldId}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={rows}
            placeholder={placeholder}
            className={cn("bg-card", mono && "font-mono text-xs")}
            disabled={saving}
          />
          {error && <p className="text-destructive">{error}</p>}
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" onClick={onSave} disabled={saving}>
              {saving && <Loader2 className="animate-spin" />}
              {saving ? "Speichere…" : "Speichern"}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={onCancel}
              disabled={saving}
            >
              Abbrechen
            </Button>
            <span className="text-xs text-muted-foreground">{editHint}</span>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          <CodeBlock className={mono ? undefined : "font-sans text-sm"}>
            {value}
          </CodeBlock>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => setEditing(true)}
            disabled={busy}
          >
            Bearbeiten
          </Button>
        </div>
      )}
      {empty && !editing && addLabel && (
        <Button
          size="sm"
          variant="secondary"
          className="mt-2"
          onClick={() => setEditing(true)}
          disabled={busy}
        >
          {addLabel}
        </Button>
      )}
      {footer}
    </section>
  );
}

function DedicationEditor({
  orderId,
  item,
  busy,
  onReload,
}: {
  orderId: string;
  item: OrderItem;
  busy: boolean;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.dedication ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(item.dedication ?? "");
  }, [item.dedication, editing]);

  async function save() {
    setSaving(true);
    setError(null);
    setMsg(null);
    try {
      const res = await ordersApi.setDedication(orderId, item.id, draft);
      setEditing(false);
      setMsg(
        res.csv_rebuilt
          ? "Gespeichert. fields.csv aktualisiert — InDesign neu rendern, damit die neue Widmung im PDF landet."
          : "Gespeichert.",
      );
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <TextFieldEditor
      title="Widmung"
      hint={
        <>
          Freitext, Zeilenumbrüche bleiben erhalten →{" "}
          <code>{"{{dedication}}"}</code>
        </>
      }
      value={item.dedication}
      emptyText="Keine Widmung hinterlegt."
      addLabel="Widmung hinzufügen"
      rows={4}
      placeholder="Widmungstext… (Enter fügt einen Zeilenumbruch ein)"
      editHint="Zeilenumbrüche werden im Druck als weiche Umbrüche übernommen."
      busy={busy}
      editing={editing}
      setEditing={setEditing}
      draft={draft}
      setDraft={setDraft}
      saving={saving}
      error={error}
      onSave={save}
      onCancel={() => {
        setEditing(false);
        setDraft(item.dedication ?? "");
        setError(null);
      }}
      footer={
        msg && <p className="mt-2 text-xs text-success-foreground">{msg}</p>
      }
    />
  );
}

/** Welche Kleidung die Figur trägt: Buch-Outfit oder Add-on „Outfit vom Foto“. */
function OutfitBadge({
  choice,
  className,
}: {
  choice: OrderItem["outfit_choice"];
  className?: string;
}) {
  return choice === "photo" ? (
    <StatusBadge
      tone="warning"
      className={className}
      title="Add-on „Outfit vom Foto“: die Figur trägt die Kleidung vom hochgeladenen Foto"
    >
      Outfit: Foto
    </StatusBadge>
  ) : (
    <StatusBadge tone="neutral" className={className} title="Vordefiniertes Outfit aus dem Buch">
      Outfit: Buch
    </StatusBadge>
  );
}

/**
 * Nur bei „Outfit vom Foto“: Kleidungs-Check, Sheet-Freigabe und die
 * exklusiven Outfit-Szenen auf einen Blick.
 */
function PhotoOutfitStatus({
  item,
  angles,
  scenes,
  bookScenes,
  busy,
  onAction,
  onGenerateScenes,
}: {
  item: OrderItem;
  angles: GenerationJob[];
  scenes: GenerationJob[];
  bookScenes: BookSceneRef[];
  busy: boolean;
  onAction: (action: "approve" | "review-mail") => void;
  onGenerateScenes: (indices: number[]) => void;
}) {
  const sheetDone = angles.some((j) => j.state === "succeeded");
  const fallback = item.photo_outfit_fallback === true;
  const approved = item.character_sheet_approved_at;
  // Outfit-Szenen ohne Job: bei Bestellungen, deren Szenen schon liefen, bevor
  // die Szenen im Buch gepflegt wurden. Ohne Szenen-Jobs kommen sie mit der
  // Freigabe ohnehin automatisch.
  const missingOutfitScenes = fallback
    ? []
    : bookScenes
        .filter((s) => s.photo_outfit_only && s.enabled)
        .filter((s) => !scenes.some((j) => j.index === s.index))
        .map((s) => s.index);
  const fmt = (ts: string) =>
    new Date(ts).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" });

  return (
    <div className="flex flex-col gap-2 rounded-lg border bg-muted/40 px-3 py-2.5 text-sm sm:flex-row sm:items-center sm:justify-between">
      <div className="space-y-0.5">
        <div>
          <span className="text-muted-foreground">Kleidungs-Check: </span>
          {item.photo_outfit_fallback == null ? (
            "noch nicht geprüft"
          ) : fallback ? (
            <>
              <StatusBadge tone="warning">Standard-Outfit</StatusBadge>{" "}
              {item.photo_outfit_fallback_reason ?? "Foto nicht verwendbar"}
            </>
          ) : (
            "Foto-Outfit"
          )}
        </div>
        <div>
          <span className="text-muted-foreground">Sheet-Freigabe: </span>
          {approved
            ? `freigegeben ${fmt(approved)}`
            : sheetDone
              ? `wartet${item.character_sheet_review_sent_at ? ` (Mail ${fmt(item.character_sheet_review_sent_at)})` : ""}`
              : "Sheet noch nicht fertig"}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {sheetDone && !approved && (
          <Button size="sm" onClick={() => onAction("approve")} disabled={busy}>
            Sheet freigeben
          </Button>
        )}
        {sheetDone && !approved && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onAction("review-mail")}
            disabled={busy}
          >
            Mail erneut senden
          </Button>
        )}
        {approved && scenes.length > 0 && missingOutfitScenes.length > 0 && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => onGenerateScenes(missingOutfitScenes)}
            disabled={busy}
          >
            Outfit-Szenen nachgenerieren ({missingOutfitScenes.length})
          </Button>
        )}
      </div>
    </div>
  );
}

/** Nur bei „Outfit vom Foto“: der Kleidungstext, mit dem das Character-Sheet eingekleidet wird. */
function PhotoOutfitDescriptionEditor({
  orderId,
  item,
  busy,
  onReload,
}: {
  orderId: string;
  item: OrderItem;
  busy: boolean;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.photo_outfit_description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(item.photo_outfit_description ?? "");
  }, [item.photo_outfit_description, editing]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await ordersApi.setPhotoOutfitDescription(orderId, item.id, draft);
      setEditing(false);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <TextFieldEditor
      title="Outfit vom Foto"
      hint={
        <>
          wird in <code>{"{{photo_outfit_description}}"}</code> des
          Character-Sheet-Prompts eingesetzt
        </>
      }
      value={item.photo_outfit_description}
      emptyText="Noch nicht extrahiert — wird vor dem Character-Sheet automatisch vom Originalfoto gelesen."
      rows={8}
      mono
      editHint="Leer speichern → wird beim nächsten Sheet-Lauf neu extrahiert (vom Foto, sonst von der Vorschau)."
      busy={busy}
      editing={editing}
      setEditing={setEditing}
      draft={draft}
      setDraft={setDraft}
      saving={saving}
      error={error}
      onSave={save}
      onCancel={() => {
        setEditing(false);
        setDraft(item.photo_outfit_description ?? "");
        setError(null);
      }}
    />
  );
}

function OutfitDescriptionEditor({
  orderId,
  item,
  busy,
  onReload,
}: {
  orderId: string;
  item: OrderItem;
  busy: boolean;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.outfit_description ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(item.outfit_description ?? "");
  }, [item.outfit_description, editing]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await ordersApi.setOutfitDescription(orderId, item.id, draft);
      setEditing(false);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <TextFieldEditor
      title="Outfit-Description"
      hint={
        <>
          wird in <code>{"{{outfit_description}}"}</code> der Szenen-Prompts
          eingesetzt
        </>
      }
      value={item.outfit_description}
      emptyText="Noch nicht extrahiert — wird nach dem ersten Character-Angle automatisch erzeugt."
      rows={10}
      mono
      editHint="Leer speichern → wird beim nächsten Angle-Neuversuch automatisch neu extrahiert."
      busy={busy}
      editing={editing}
      setEditing={setEditing}
      draft={draft}
      setDraft={setDraft}
      saving={saving}
      error={error}
      onSave={save}
      onCancel={() => {
        setEditing(false);
        setDraft(item.outfit_description ?? "");
        setError(null);
      }}
      footer={
        <ReextractButton
          orderId={orderId}
          itemId={item.id}
          target="outfit"
          label="Outfit neu extrahieren"
          busy={busy}
          onReload={onReload}
        />
      }
    />
  );
}

function ReextractButton({
  orderId,
  itemId,
  target,
  label,
  busy,
  onReload,
}: {
  orderId: string;
  itemId: string;
  target: "outfit" | "identity";
  label: string;
  busy: boolean;
  onReload: () => void;
}) {
  const confirm = useConfirm();
  const [running, setRunning] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    if (
      !(await confirm({
        title: label,
        description: `${label}: aus dem ersten Character-Angle neu extrahieren? Der aktuelle Wert wird überschrieben.`,
        confirmLabel: "Neu extrahieren",
      }))
    )
      return;
    setRunning(true);
    setMsg(null);
    setError(null);
    try {
      const res = await ordersApi.reextractDescriptions(
        orderId,
        itemId,
        target,
      );
      if (res.errors && res.errors.length > 0) {
        setError(res.errors.join("; "));
      } else {
        setMsg("Neu extrahiert.");
      }
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <Button
        size="sm"
        variant="secondary"
        onClick={run}
        disabled={busy || running}
      >
        {running && <Loader2 className="animate-spin" />}
        {running ? "Extrahiere…" : label}
      </Button>
      <span className="text-xs text-muted-foreground">
        Erneute Vision-Extraktion aus dem 1. Character-Angle.
      </span>
      {msg && <span className="text-xs text-success-foreground">{msg}</span>}
      {error && <span className="text-xs text-destructive">{error}</span>}
    </div>
  );
}

function CharIdentityEditor({
  orderId,
  item,
  busy,
  onReload,
}: {
  orderId: string;
  item: OrderItem;
  busy: boolean;
  onReload: () => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.char_identity ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!editing) setDraft(item.char_identity ?? "");
  }, [item.char_identity, editing]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await ordersApi.setCharIdentity(orderId, item.id, draft);
      setEditing(false);
      onReload();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <TextFieldEditor
      title="Char-Identity"
      hint={
        <>
          wird in <code>{"{{char_identity}}"}</code> der Szenen-Prompts
          eingesetzt
        </>
      }
      value={item.char_identity}
      emptyText="Noch nicht extrahiert — wird nach dem ersten Character-Angle automatisch erzeugt."
      rows={3}
      mono
      editHint="Leer speichern → wird beim nächsten Angle-Neuversuch automatisch neu extrahiert."
      busy={busy}
      editing={editing}
      setEditing={setEditing}
      draft={draft}
      setDraft={setDraft}
      saving={saving}
      error={error}
      onSave={save}
      onCancel={() => {
        setEditing(false);
        setDraft(item.char_identity ?? "");
        setError(null);
      }}
      footer={
        <ReextractButton
          orderId={orderId}
          itemId={item.id}
          target="identity"
          label="Identity neu extrahieren"
          busy={busy}
          onReload={onReload}
        />
      }
    />
  );
}

const INDESIGN_STATE_LABELS: Record<IndesignJob["state"], string> = {
  pending: "Wartet auf Mac",
  claimed: "Mac hat geclaimt",
  rendering: "InDesign rendert",
  completed: "PDFs fertig",
  failed: "Fehlgeschlagen",
};

const INDESIGN_STATE_TONE: Record<IndesignJob["state"], Tone> = {
  pending: "neutral",
  claimed: "info",
  rendering: "info",
  completed: "success",
  failed: "critical",
};

function IndesignBlock({
  orderId,
  itemId,
  job,
  onRetry,
  busy,
}: {
  orderId: string;
  itemId: string;
  job: IndesignJob | null;
  onRetry: (jobId: string) => void;
  busy: boolean;
}) {
  if (!job) {
    return (
      <section>
        <SectionTitle>InDesign</SectionTitle>
        <p className="text-muted-foreground">
          Noch kein InDesign-Job für dieses Item — wartet auf Asset-Generierung.
        </p>
      </section>
    );
  }

  async function download(kind: "cover" | "bookblock") {
    const filename = `${kind}_${itemId.slice(0, 8)}.pdf`;
    try {
      await downloadAuthed(pdfUrl(orderId, itemId, kind), filename);
    } catch (err) {
      toast.error(`Download fehlgeschlagen: ${(err as Error).message}`);
    }
  }

  return (
    <section>
      <SectionTitle
        actions={
          <StatusBadge tone={INDESIGN_STATE_TONE[job.state]}>
            {INDESIGN_STATE_LABELS[job.state]}
          </StatusBadge>
        }
      >
        InDesign
      </SectionTitle>

      <MetaList>
        {job.claimed_by && <MetaRow label="Worker">{job.claimed_by}</MetaRow>}
        {job.claimed_at && (
          <MetaRow label="Geclaimt">
            {new Date(job.claimed_at).toLocaleString()}
          </MetaRow>
        )}
        {job.started_at && (
          <MetaRow label="Render-Start">
            {new Date(job.started_at).toLocaleString()}
          </MetaRow>
        )}
        {job.heartbeat_at && (
          <MetaRow label="Letzter Heartbeat">
            {new Date(job.heartbeat_at).toLocaleString()}
          </MetaRow>
        )}
        {job.finished_at && (
          <MetaRow label="Fertig">
            {new Date(job.finished_at).toLocaleString()}
          </MetaRow>
        )}
        <MetaRow label="Versuche">{job.attempts}</MetaRow>
      </MetaList>

      {job.placeholder_scenes > 0 && job.state === "completed" && (
        <Alert variant="warning" className="mt-3">
          <AlertTriangle />
          <AlertTitle>Platzhalter-Render</AlertTitle>
          <AlertDescription>
            {job.placeholder_scenes} Szene(n) fehlen und erscheinen als
            Vorlagen-Platzhalter. Nur zur Ansicht – nicht freigebbar. Nach den
            Szenen wird automatisch neu gerendert.
          </AlertDescription>
        </Alert>
      )}

      {job.last_error && (
        <Alert variant="critical" className="mt-3">
          <AlertTriangle />
          <AlertDescription>
            <span className="font-medium">Letzter Fehler:</span>{" "}
            <code className="font-mono text-xs break-all">
              {job.last_error}
            </code>
          </AlertDescription>
        </Alert>
      )}

      <div className="mt-3 flex flex-wrap gap-2">
        {job.state === "completed" && (
          <>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => download("cover")}
              disabled={busy}
            >
              <FileText />
              Cover PDF
              {job.cover_pdf_bytes != null && (
                <span className="text-muted-foreground">
                  · {prettyBytes(job.cover_pdf_bytes)}
                </span>
              )}
            </Button>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => download("bookblock")}
              disabled={busy}
            >
              <FileText />
              Bookblock PDF
              {job.bookblock_pdf_bytes != null && (
                <span className="text-muted-foreground">
                  · {prettyBytes(job.bookblock_pdf_bytes)}
                </span>
              )}
            </Button>
          </>
        )}
        {(job.state === "failed" || job.state === "completed") && (
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onRetry(job.id)}
            disabled={busy}
          >
            {job.state === "failed" ? "Erneut versuchen" : "Neu rendern"}
          </Button>
        )}
      </div>
    </section>
  );
}

function canBuildPrintPackage(
  items: OrderItem[],
  indesignJobs: IndesignJob[],
): boolean {
  if (items.length === 0) return false;
  const completed = new Set(
    indesignJobs
      .filter((j) => j.state === "completed")
      .map((j) => j.order_item_id),
  );
  return items.every((it) => completed.has(it.id));
}

function canStartIndesign(items: OrderItem[]): boolean {
  if (items.length === 0) return false;
  return items.some((it) => Boolean(it.fields_csv_path));
}

/**
 * Sendungsverfolgungs-Link für die Carrier, die die Druckerei in der
 * SC-XML meldet. Unbekannter Carrier → null, dann wird die Nummer nur
 * angezeigt statt verlinkt.
 */
function trackingUrl(carrier: string | null, tracking: string): string | null {
  const c = (carrier ?? "").toLowerCase();
  const no = encodeURIComponent(tracking);
  if (/warenpost|dhl|deutsche post/.test(c)) {
    return `https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html?piececode=${no}`;
  }
  if (/dpd/.test(c)) return `https://tracking.dpd.de/status/de_DE/parcel/${no}`;
  if (/gls/.test(c))
    return `https://gls-group.eu/DE/de/paketverfolgung?match=${no}`;
  if (/ups/.test(c))
    return `https://www.ups.com/track?loc=de_DE&tracknum=${no}`;
  if (/hermes/.test(c))
    return `https://www.myhermes.de/empfangen/sendungsverfolgung/#${no}`;
  return null;
}

function prettyBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

interface ProgressSummary {
  total: number;
  succeeded: number;
  running: number;
  failed: number;
  pending: number;
}

/**
 * One row of the scene grid: a scene of the book plus the generation job that
 * belongs to it, if any. Scenes without a job are the interesting case — they
 * are what an order looks like when the scene was deactivated at intake, and
 * they are rendered as an empty tile with a "Generieren" button.
 */
interface SceneEntry {
  index: number;
  scene: BookSceneRef | null;
  job: GenerationJob | null;
}

/**
 * Join the book's scene catalog with the item's scene jobs. Driven by the
 * catalog (so deactivated and never-generated scenes appear), with any job
 * whose scene no longer exists in the book appended at the end so an existing
 * render never silently disappears from the page.
 */
function buildSceneEntries(
  catalog: BookSceneRef[],
  jobs: GenerationJob[],
): SceneEntry[] {
  const jobByIndex = new Map(
    jobs.filter((j) => j.index != null).map((j) => [j.index as number, j]),
  );
  const entries: SceneEntry[] = catalog
    .slice()
    .sort((a, b) => a.order_index - b.order_index || a.index - b.index)
    .map((scene) => ({
      index: scene.index,
      scene,
      job: jobByIndex.get(scene.index) ?? null,
    }));

  const known = new Set(catalog.map((s) => s.index));
  for (const job of jobs) {
    if (job.index == null || known.has(job.index)) continue;
    entries.push({ index: job.index, scene: null, job });
  }
  return entries;
}

/**
 * Progress over the scene catalog rather than over existing jobs: a scene that
 * was never queued counts as outstanding. Deactivated scenes only count once
 * they actually have a job — they are not part of the automatic run.
 */
function summarizeScenes(entries: SceneEntry[]): ProgressSummary {
  const relevant = entries.filter((e) => e.scene?.enabled !== false || e.job);
  const s = {
    total: relevant.length,
    succeeded: 0,
    running: 0,
    failed: 0,
    pending: 0,
  };
  for (const e of relevant) {
    if (e.job?.state === "succeeded") s.succeeded++;
    else if (e.job?.state === "running") s.running++;
    else if (e.job?.state === "failed") s.failed++;
    else s.pending++;
  }
  return s;
}

function summarize(jobs: GenerationJob[]): ProgressSummary {
  const s = {
    total: jobs.length,
    succeeded: 0,
    running: 0,
    failed: 0,
    pending: 0,
  };
  for (const j of jobs) {
    if (j.state === "succeeded") s.succeeded++;
    else if (j.state === "running") s.running++;
    else if (j.state === "failed") s.failed++;
    else s.pending++;
  }
  return s;
}

function ProgressRow({
  label,
  summary,
}: {
  label: string;
  summary: ProgressSummary;
}) {
  if (summary.total === 0) return null;
  const pct = Math.round((summary.succeeded / summary.total) * 100);
  return (
    <div className="grid grid-cols-[120px_1fr_auto] items-center gap-3 text-xs sm:grid-cols-[140px_1fr_200px]">
      <span className="text-muted-foreground">{label}</span>
      <div
        className="h-2 overflow-hidden rounded-full bg-accent"
        role="progressbar"
        aria-label={label}
        aria-valuemin={0}
        aria-valuemax={summary.total}
        aria-valuenow={summary.succeeded}
      >
        <div
          className={cn(
            "h-full rounded-full transition-[width] duration-300",
            pct === 100 ? "bg-[#29845a]" : "bg-ring",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="tabular-nums">
        {summary.succeeded}/{summary.total}
        {summary.failed > 0 && (
          <span className="text-destructive">
            {" "}
            · {summary.failed} fehlgeschlagen
          </span>
        )}
        {summary.running > 0 && (
          <span className="text-ring"> · {summary.running} läuft</span>
        )}
      </span>
    </div>
  );
}

// ── Asset-Kacheln ───────────────────────────────────────────────────────────

const TILE_PLACEHOLDER =
  "flex aspect-square w-full items-center justify-center bg-muted text-xs text-muted-foreground";

const JOB_STATE_LABELS: Record<string, string> = {
  succeeded: "fertig",
  running: "läuft",
  pending: "wartet",
  failed: "Fehler",
  skipped: "übersprungen",
  missing: "nicht erstellt",
};

const JOB_STATE_TONE: Record<string, Tone> = {
  succeeded: "success",
  running: "info",
  pending: "neutral",
  failed: "critical",
  skipped: "neutral",
  missing: "neutral",
};

function Tile({
  state,
  children,
}: {
  state: string;
  children: React.ReactNode;
}) {
  return (
    <figure
      className={cn(
        "m-0 flex flex-col overflow-hidden rounded-lg border bg-card",
        state === "failed" && "border-destructive/40",
        state === "missing" && "border-dashed",
      )}
    >
      {children}
    </figure>
  );
}

function TileCaption({ children }: { children: React.ReactNode }) {
  return (
    <figcaption className="space-y-1.5 p-2 text-xs">{children}</figcaption>
  );
}

function TileError({ message }: { message: string }) {
  return (
    <div className="line-clamp-2 text-destructive" title={message}>
      {message.slice(0, 80)}
    </div>
  );
}

/** Character-angle grid. Scenes have their own row — see {@link SceneRow}. */
function AssetRow({
  title,
  jobs,
  orderId,
  itemId,
  kind,
  onRetry,
  onEditImage,
  onRegenerateSheet,
  busy,
}: {
  title: string;
  jobs: GenerationJob[];
  orderId: string;
  itemId: string;
  kind: "angles";
  onRetry: (jobId: string) => void;
  onEditImage?: (jobId: string, prompt: string) => Promise<void>;
  onRegenerateSheet?: (jobId: string) => void;
  busy: boolean;
}) {
  // Editing overwrites the asset file at the same URL, so the browser/AuthAsset
  // would otherwise show the stale cached image. Bump a per-job version to force
  // a re-fetch after a successful edit.
  const [bust, setBust] = useState<Record<string, number>>({});

  return (
    <div>
      <SectionTitle>{title}</SectionTitle>
      <div className="grid grid-cols-[repeat(auto-fill,minmax(150px,1fr))] gap-3">
        {jobs.map((j) => {
          // The filename never changes, so a re-render would otherwise show the
          // cached image. `finished_at` covers regeneration through the queue,
          // `bust` covers the synchronous in-place edit.
          const version = bust[j.id] ?? j.finished_at ?? "";
          const url =
            assetUrl(orderId, itemId, kind, `angle_${j.index}.png`) +
            (version ? `?v=${encodeURIComponent(String(version))}` : "");
          return (
            <Tile key={j.id} state={j.state}>
              {j.state === "succeeded" ? (
                <AuthAsset
                  url={url}
                  alt={`${kind} ${j.index}`}
                  className="aspect-square w-full object-cover"
                  placeholderClassName={TILE_PLACEHOLDER}
                />
              ) : (
                <div className={TILE_PLACEHOLDER}>
                  {j.state === "running" && "läuft…"}
                  {j.state === "pending" && "wartet"}
                  {j.state === "failed" && "Fehler"}
                  {j.state === "skipped" && "übersprungen"}
                </div>
              )}
              <TileCaption>
                <div className="flex items-center justify-between gap-1">
                  <span className="font-medium">#{j.index}</span>
                  <StatusBadge tone={JOB_STATE_TONE[j.state] ?? "neutral"}>
                    {JOB_STATE_LABELS[j.state] ?? j.state}
                  </StatusBadge>
                </div>
                <div className="flex flex-wrap gap-1">
                  {j.state === "failed" && (
                    <Button
                      size="xs"
                      variant="secondary"
                      onClick={() => onRetry(j.id)}
                      disabled={busy}
                    >
                      Erneut versuchen
                    </Button>
                  )}
                  {/* Full re-render from the original child photo. Offered for
                      every settled state — after a failure it's the escalation
                      from "Retry", after success it applies a changed prompt. */}
                  {onRegenerateSheet &&
                    j.state !== "pending" &&
                    j.state !== "running" && (
                      <Button
                        size="xs"
                        variant="secondary"
                        onClick={() => onRegenerateSheet(j.id)}
                        disabled={busy}
                        title="Erzeugt das Character-Sheet komplett neu aus dem ursprünglich hochgeladenen Kinderfoto, mit dem aktuellen Prompt. Szenen bleiben unverändert."
                      >
                        Neu generieren
                      </Button>
                    )}
                </div>
                {j.last_error && <TileError message={j.last_error} />}
                {onEditImage && j.state === "succeeded" && (
                  <AngleEditForm
                    busy={busy}
                    onSubmit={async (prompt) => {
                      await onEditImage(j.id, prompt);
                      setBust((b) => ({ ...b, [j.id]: Date.now() }));
                    }}
                  />
                )}
              </TileCaption>
            </Tile>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Scene grid, driven by the book's scene catalog instead of by the existing
 * jobs. Every scene of the book gets a tile — including the ones that were
 * deactivated at order intake and therefore never produced an image. Each tile
 * can be generated on demand, so "deaktiviert" only ever means "not generated
 * automatically", never "not generatable".
 */
function SceneRow({
  entries,
  orderId,
  itemId,
  onGenerate,
  busy,
  qualityChecks,
  onQcRecheck,
  onQcApprove,
  onQcRegenerate,
}: {
  entries: SceneEntry[];
  orderId: string;
  itemId: string;
  onGenerate: (indices: number[]) => void;
  busy: boolean;
  qualityChecks?: QualityCheckJob[];
  onQcRecheck?: (qcJobId: string) => void;
  onQcApprove?: (qcJobId: string) => void;
  onQcRegenerate?: (qcJobId: string) => void;
}) {
  const confirm = useConfirm();

  if (entries.length === 0) {
    return (
      <section>
        <SectionTitle>Szenen</SectionTitle>
        <p className="text-muted-foreground">
          Für dieses Buch sind keine Szenen hinterlegt.
        </p>
      </section>
    );
  }

  // Two separate bulk actions so "inaktiv" keeps its meaning: the plain one
  // covers the scenes that belong to the automatic run, the second one is the
  // explicit, confirmed opt-in for the deactivated ones.
  const withoutImage = (e: SceneEntry) =>
    !e.job || e.job.state === "failed" || e.job.state === "skipped";
  const missing = entries
    .filter((e) => e.scene?.enabled === true && withoutImage(e))
    .map((e) => e.index);
  const missingInactive = entries
    .filter((e) => e.scene?.enabled === false && withoutImage(e))
    .map((e) => e.index);

  async function generateInactive() {
    const ok = await confirm({
      title: `${missingInactive.length} inaktive Szene(n) jetzt generieren?`,
      description:
        "Diese Szenen laufen bei Ordereingang nicht automatisch mit. " +
        "Manuell erzeugte Bilder landen anschließend auch in der fields.csv.",
      confirmLabel: "Generieren",
    });
    if (ok) onGenerate(missingInactive);
  }

  // The automatic run submits all scenes of an item as one OpenAI batch, which
  // can take hours. Without this banner a whole grid of "läuft…" tiles is
  // indistinguishable from a stuck pipeline.
  const openBatch = entries
    .map((e) => e.job?.batch ?? null)
    .find((b) => b && batchPhase(b) === "open");
  const scenesInBatch = openBatch
    ? entries.filter((e) => e.job?.batch?.id === openBatch.id).length
    : 0;

  return (
    <section>
      <SectionTitle
        actions={
          <>
            {missing.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={() => onGenerate(missing)}
                title="Generiert alle aktiven Szenen, die noch kein Bild haben. Deaktivierte Szenen bleiben außen vor — die startest du einzeln."
              >
                Fehlende generieren ({missing.length})
              </Button>
            )}
            {missingInactive.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                disabled={busy}
                onClick={generateInactive}
                title="Generiert die Szenen, die für den automatischen Lauf deaktiviert sind — z.B. wenn sie bei Ordereingang aus waren."
              >
                Inaktive generieren ({missingInactive.length})
              </Button>
            )}
          </>
        }
      >
        Szenen
      </SectionTitle>
      {openBatch && (
        <Alert variant="info" className="mb-3">
          <span className="relative mt-1 flex size-2" aria-hidden="true">
            <span className="absolute inline-flex size-full animate-ping rounded-full bg-current opacity-60" />
            <span className="relative inline-flex size-2 rounded-full bg-current" />
          </span>
          <AlertDescription>
            <span>
              <strong>OpenAI-Batch läuft</strong> — {scenesInBatch} Szene(n)
              eingereicht vor {sinceLabel(openBatch.submitted_at)}
              {openBatch.last_polled_at
                ? `, zuletzt geprüft vor ${sinceLabel(openBatch.last_polled_at)}`
                : ", noch nicht geprüft"}
              . Der automatische Lauf wartet auf das Ergebnis (bis zu 24 h).
              Einzelne Szenen kannst du jederzeit von Hand generieren — die
              laufen sofort über die normale API und werden vom Batch nicht mehr
              überschrieben.
            </span>
          </AlertDescription>
        </Alert>
      )}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {entries.map((e) => {
          const job = e.job;
          const url = assetUrl(
            orderId,
            itemId,
            "scenes",
            `scene_${e.index}.png`,
          );
          const qc =
            qualityChecks?.find((q) => q.scene_index === e.index) ?? null;
          const disabled = e.scene?.enabled === false;
          const state = job?.state ?? "missing";
          const phase = batchPhase(job?.batch ?? null);
          // Ohne Bild reicht eine flache Fläche; geladen wird im Bildformat.
          const sceneTilePlaceholder = cn(TILE_PLACEHOLDER, "aspect-[3/1]");
          return (
            <Tile key={e.scene?.id ?? job?.id ?? e.index} state={state}>
              {job?.state === "succeeded" ? (
                <AuthAsset
                  url={url}
                  alt={`scene ${e.index}`}
                  className="h-auto w-full bg-muted object-contain"
                  placeholderClassName={sceneTilePlaceholder}
                />
              ) : (
                <div className={sceneTilePlaceholder}>
                  {state === "running" &&
                    (phase === "open" ? "im Batch…" : "läuft…")}
                  {state === "pending" && "wartet"}
                  {state === "failed" && "Fehler"}
                  {state === "skipped" && "übersprungen"}
                  {state === "missing" && "nicht erstellt"}
                </div>
              )}
              <TileCaption>
                <div className="flex flex-wrap items-center gap-1">
                  <span className="mr-auto font-medium">#{e.index}</span>
                  <StatusBadge tone={JOB_STATE_TONE[state] ?? "neutral"}>
                    {JOB_STATE_LABELS[state] ?? state}
                  </StatusBadge>
                  {job && <SceneBatchBadge batch={job.batch} state={state} />}
                  {disabled && (
                    <StatusBadge
                      tone="warning"
                      title="Diese Szene wird bei Ordereingang nicht automatisch generiert — manuell geht sie trotzdem."
                    >
                      inaktiv
                    </StatusBadge>
                  )}
                  {!e.scene && (
                    <StatusBadge
                      tone="neutral"
                      title="Diese Szene existiert im Buch nicht mehr — das Bild stammt aus einem früheren Lauf."
                    >
                      entfernt
                    </StatusBadge>
                  )}
                </div>
                {e.scene && (
                  <div
                    className="truncate text-muted-foreground"
                    title={e.scene.name}
                  >
                    {e.scene.name}
                  </div>
                )}
                {e.scene && (
                  <Button
                    size="xs"
                    variant="secondary"
                    onClick={() => onGenerate([e.index])}
                    disabled={busy || state === "running"}
                    title={
                      disabled
                        ? "Szene ist für die automatische Generierung deaktiviert — hier startest du sie trotzdem von Hand."
                        : undefined
                    }
                  >
                    {job?.state === "succeeded"
                      ? "Neu generieren"
                      : "Generieren"}
                  </Button>
                )}
                {job?.last_error && <TileError message={job.last_error} />}
                {qc && (
                  <QualityCheckBadge
                    qc={qc}
                    busy={busy}
                    onRecheck={onQcRecheck}
                    onApprove={onQcApprove}
                    onRegenerate={onQcRegenerate}
                  />
                )}
              </TileCaption>
            </Tile>
          );
        })}
      </div>
    </section>
  );
}

/**
 * Where a scene stands in its OpenAI batch, as far as the operator cares:
 *   "open"   → submitted, result still outstanding (the long wait)
 *   "failed" → the batch itself died; the scene will never arrive from it
 *   null     → not batched at all (live API), or the batch is done and the
 *              job's own state already tells the whole story.
 */
function batchPhase(
  batch: GenerationJobBatch | null,
): "open" | "failed" | null {
  if (!batch) return null;
  if (batch.state === "submitted" || batch.state === "in_progress")
    return "open";
  if (
    batch.state === "failed" ||
    batch.state === "expired" ||
    batch.state === "cancelled"
  ) {
    return "failed";
  }
  return null;
}

/** Compact "vor 2 h 14 min" for a timestamp. */
function sinceLabel(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "—";
  const minutes = Math.floor(ms / 60_000);
  if (minutes < 1) return "wenigen Sekunden";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  return `${hours} h ${minutes % 60} min`;
}

/**
 * Per-scene marker for the batch a scene was submitted in. Only rendered when
 * it adds something: a scene waiting at OpenAI, or a batch that failed. A
 * scene that ran on the live API (manual generation, Gemini) shows nothing —
 * absence of the badge is itself the signal that it was NOT batched.
 */
function SceneBatchBadge({
  batch,
  state,
}: {
  batch: GenerationJobBatch | null;
  state: string;
}) {
  const phase = batchPhase(batch);
  if (!batch || !phase) {
    // A finished scene that never belonged to a batch was generated by hand.
    if (!batch && (state === "succeeded" || state === "running")) {
      return (
        <StatusBadge
          tone="neutral"
          title="Diese Szene lief über die normale API (manuell generiert oder Gemini), nicht über den Batch."
        >
          Live-API
        </StatusBadge>
      );
    }
    return null;
  }

  if (phase === "failed") {
    return (
      <StatusBadge
        tone="critical"
        title={batch.last_error ?? `Batch ${batch.state}`}
      >
        Batch {batch.state}
      </StatusBadge>
    );
  }

  return (
    <StatusBadge
      tone="info"
      title={
        `OpenAI-Batch ${batch.batch_id}\n` +
        `${batch.request_count} Szene(n), eingereicht vor ${sinceLabel(batch.submitted_at)}.\n` +
        "Ergebnisse kommen asynchron (bis zu 24 h)."
      }
    >
      Batch läuft · {sinceLabel(batch.submitted_at)}
    </StatusBadge>
  );
}

// Inline "Editieren" affordance on a character-angle tile: a toggle button that
// reveals a prompt textarea. Submitting sends the prompt to OpenAI (via the
// parent handler), which overwrites the angle image in place.
function AngleEditForm({
  onSubmit,
  busy,
}: {
  onSubmit: (prompt: string) => Promise<void>;
  busy: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useUnsavedChanges(open && prompt.trim().length > 0);

  if (!open) {
    return (
      <Button
        size="xs"
        variant="secondary"
        disabled={busy}
        onClick={() => setOpen(true)}
      >
        Editieren
      </Button>
    );
  }

  const submit = async () => {
    const p = prompt.trim();
    if (!p) return;
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit(p);
      setOpen(false);
      setPrompt("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Editieren fehlgeschlagen.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Textarea
        rows={2}
        value={prompt}
        placeholder="Änderung beschreiben, z.B. „mach die Haare länger“"
        aria-label="Änderung am Bild beschreiben"
        disabled={submitting}
        onChange={(e) => setPrompt(e.target.value)}
        className="min-h-0 bg-card text-xs"
      />
      <div className="flex gap-1">
        <Button
          size="xs"
          onClick={submit}
          disabled={submitting || !prompt.trim()}
        >
          {submitting ? "läuft…" : "Senden"}
        </Button>
        <Button
          size="xs"
          variant="secondary"
          onClick={() => {
            setOpen(false);
            setError(null);
          }}
          disabled={submitting}
        >
          Abbrechen
        </Button>
      </div>
      {error && <div className="text-destructive">{error}</div>}
    </div>
  );
}

const QC_LABELS: Record<QualityCheckJob["state"], string> = {
  pending: "QC: wartet",
  checking: "QC: läuft",
  approved: "QC: ok",
  needs_rework: "QC: nicht ok",
  failed: "QC: System-Fehler",
};

const QC_TONE: Record<QualityCheckJob["state"], Tone> = {
  pending: "neutral",
  checking: "info",
  approved: "success",
  needs_rework: "critical",
  failed: "warning",
};

function QualityCheckBadge({
  qc,
  busy,
  onRecheck,
  onApprove,
  onRegenerate,
}: {
  qc: QualityCheckJob;
  busy: boolean;
  onRecheck?: (qcJobId: string) => void;
  onApprove?: (qcJobId: string) => void;
  onRegenerate?: (qcJobId: string) => void;
}) {
  const failed = qc.state === "needs_rework" || qc.state === "failed";
  const reasons = qc.check_result
    ? Object.entries(qc.check_result.checks)
        .filter(([, v]) => !v.pass)
        .map(([k, v]) => `${k}: ${v.reason ?? "fail"}`)
        .join("\n")
    : (qc.last_error ?? "");

  return (
    <div
      className="space-y-1.5 rounded-md border bg-muted/60 p-1.5"
      title={reasons || undefined}
    >
      <div className="flex flex-wrap items-center gap-1">
        <StatusBadge tone={QC_TONE[qc.state]}>
          {QC_LABELS[qc.state]}
          {qc.manual_override && qc.state === "approved" && <span> *</span>}
        </StatusBadge>
        {qc.auto_regen_attempts > 0 && (
          <span className="text-muted-foreground">
            automatisch neu generiert: {qc.auto_regen_attempts}×
          </span>
        )}
      </div>
      {failed && reasons && (
        <pre className="m-0 max-h-24 overflow-auto font-mono text-[11px] whitespace-pre-wrap text-critical-foreground">
          {reasons}
        </pre>
      )}
      <div className="flex flex-wrap gap-1">
        {onRecheck &&
          (qc.state === "needs_rework" ||
            qc.state === "failed" ||
            qc.state === "approved") && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => onRecheck(qc.id)}
              disabled={busy}
            >
              Erneut prüfen
            </Button>
          )}
        {onApprove &&
          (qc.state === "needs_rework" || qc.state === "failed") && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => onApprove(qc.id)}
              disabled={busy}
            >
              Trotzdem freigeben
            </Button>
          )}
        {onRegenerate &&
          (qc.state === "needs_rework" || qc.state === "failed") && (
            <Button
              size="xs"
              variant="secondary"
              onClick={() => onRegenerate(qc.id)}
              disabled={busy}
            >
              Neu generieren
            </Button>
          )}
      </div>
    </div>
  );
}
